import { useState, useEffect } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import type { VectorStoreResponse as VectorStore } from "../../api/generated";

export interface ScopeOption {
  label: string;
  scope: "org" | "team";
  scopeOwnerId: string | null;
}

export interface VectorStoreSubmit {
  name: string;
  description: string;
  scope?: "org" | "team";
  scopeOwnerId?: string | null;
}

interface VectorStoreDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: VectorStoreSubmit) => void;
  store?: VectorStore | null;
  isLoading?: boolean;
  scopeOptions?: ScopeOption[];
}

const SCOPE_LABELS: Record<string, string> = {
  org: "Organization",
  team: "Team",
  individual: "Personal",
  system: "System",
};

export function VectorStoreDialog({
  open,
  onClose,
  onSubmit,
  store,
  isLoading,
  scopeOptions = [],
}: VectorStoreDialogProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [scopeIndex, setScopeIndex] = useState(0);

  useEffect(() => {
    if (store) {
      setName(store.name);
      setDescription(store.description);
    } else {
      setName("");
      setDescription("");
      setScopeIndex(0);
    }
  }, [store, open]);

  const handleSubmit = () => {
    if (!name.trim()) return;
    const data: VectorStoreSubmit = {
      name: name.trim(),
      description: description.trim(),
    };
    // Scope is chosen on create only (it's fixed for an existing KB).
    const opt = scopeOptions[scopeIndex] ?? scopeOptions[0];
    if (!store && opt) {
      data.scope = opt.scope;
      data.scopeOwnerId = opt.scopeOwnerId;
    }
    onSubmit(data);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {store ? "Edit Knowledge Base" : "New Knowledge Base"}
      </DialogTitle>
      <DialogContent>
        {!store && scopeOptions.length > 0 && (
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel id="kb-scope-label">Scope</InputLabel>
            <Select
              labelId="kb-scope-label"
              label="Scope"
              value={scopeIndex}
              onChange={(e) => setScopeIndex(Number(e.target.value))}
            >
              {scopeOptions.map((opt, i) => (
                <MenuItem key={`${opt.scope}:${opt.scopeOwnerId ?? ""}`} value={i}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {store && (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mt: 1
            }}>
            Scope: {SCOPE_LABELS[store.scope ?? "org"] ?? store.scope}
          </Typography>
        )}
        <TextField
          autoFocus
          label="Name"
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ mt: 2 }}
          slotProps={{
            htmlInput: { maxLength: 100 }
          }}
        />
        <TextField
          label="Description"
          fullWidth
          multiline
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!name.trim() || isLoading}
        >
          {store ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
